import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMySubscription } from '../services/api';
import AppShell from '../components/layout/AppShell';
import './Subscription.css';

function daysBetween(future) {
  if (!future) return 0;
  const ms = new Date(future).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function isFuture(d) {
  if (!d) return false;
  return new Date(d).getTime() > Date.now();
}

const Subscription = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getMySubscription()
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch((err) => {
        console.error('Failed to load subscription', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <AppShell><div className="sub-page">
        <div className="sub-loading">{t('subscription.loading')}</div>
      </div>
    </AppShell>
    );
  }

  const sub = data?.subscription;
  const restaurant = data?.restaurant;

  if (!restaurant) {
    return (
      <AppShell><div className="sub-page">
        <div className="sub-card">
          <h1>{t('subscription.title')}</h1>
          <p className="sub-muted">{t('subscription.noRestaurant')}</p>
          <Link to="/manager/dashboard" className="btn btn-primary">{t('subscription.backToDashboard')}</Link>
        </div>
      </div>
    </AppShell>
    );
  }

  if (!sub) {
    return (
      <AppShell><div className="sub-page">
        <div className="sub-card">
          <h1>{t('subscription.title')}</h1>
          <p className="sub-muted">{t('subscription.notStarted')}</p>
          <p className="sub-muted">{t('subscription.contactAdmin')}</p>
          <Link to="/manager/dashboard" className="btn btn-primary">{t('subscription.backToDashboard')}</Link>
        </div>
      </div>
    </AppShell>
    );
  }

  const status = sub.status;
  // Compute the displayed status: trial that ran out is shown as "expired".
  const trialExpired = status === 'trialing' && sub.trial_ends_at && !isFuture(sub.trial_ends_at);
  const activeExpired = status === 'active' && sub.current_period_end && !isFuture(sub.current_period_end);
  const displayStatus = trialExpired ? 'expired_trial' : activeExpired ? 'expired' : status;

  const planName = sub.plan?.display_name || t('home.pricing.starter.name');
  const monthly = (sub.plan?.monthly_price_cents || 0) / 100;
  const yearly = (sub.plan?.yearly_price_cents || 0) / 100;
  const currency = sub.plan?.currency || 'USD';

  const renderStatusPill = () => (
    <span className={`sub-status sub-status-${displayStatus}`}>
      {t(`subscription.status.${displayStatus}`, displayStatus)}
    </span>
  );

  return (
    <AppShell><div className="sub-page">
      <div className="sub-card">
        <header className="sub-header">
          <div>
            <h1>{t('subscription.title')}</h1>
            <p className="sub-muted">{t('subscription.subtitle', { restaurant: restaurant.name })}</p>
          </div>
          {renderStatusPill()}
        </header>

        <div className="sub-summary">
          <div className="sub-summary-row">
            <span className="sub-summary-label">{t('subscription.plan')}</span>
            <span className="sub-summary-value">{planName}</span>
          </div>
          <div className="sub-summary-row">
            <span className="sub-summary-label">{t('subscription.priceMonthly')}</span>
            <span className="sub-summary-value">{currency} {monthly.toFixed(2)}</span>
          </div>
          <div className="sub-summary-row">
            <span className="sub-summary-label">{t('subscription.priceYearly')}</span>
            <span className="sub-summary-value">{currency} {yearly.toFixed(2)}</span>
          </div>
          {status === 'trialing' && sub.trial_ends_at && !trialExpired && (
            <div className="sub-summary-row">
              <span className="sub-summary-label">{t('subscription.trialEnds')}</span>
              <span className="sub-summary-value">
                {new Date(sub.trial_ends_at).toLocaleDateString(locale)}
                {' · '}
                {t('subscription.daysLeft', { count: daysBetween(sub.trial_ends_at) })}
              </span>
            </div>
          )}
          {status === 'active' && sub.current_period_end && !activeExpired && (
            <div className="sub-summary-row">
              <span className="sub-summary-label">
                {sub.cancel_at_period_end ? t('subscription.endsOn') : t('subscription.renewsOn')}
              </span>
              <span className="sub-summary-value">
                {new Date(sub.current_period_end).toLocaleDateString(locale)}
              </span>
            </div>
          )}
        </div>

        {(displayStatus === 'expired_trial' || displayStatus === 'expired' ||
          status === 'past_due' || status === 'canceled' ||
          (status === 'active' && sub.cancel_at_period_end)) && (
          <div className="sub-callout sub-callout-warning">
            <strong>{t('subscription.payOffline.title')}</strong>
            <p>{t('subscription.payOffline.message')}</p>
          </div>
        )}

        {status === 'trialing' && !trialExpired && (
          <div className="sub-callout">
            <strong>{t('subscription.contactAdminTitle')}</strong>
            <p>{t('subscription.contactAdmin')}</p>
          </div>
        )}

        <p className="sub-back">
          <Link to="/manager/dashboard">{t('subscription.backToDashboard')}</Link>
        </p>
      </div>
    </div>
    </AppShell>
  );
};

export default Subscription;
